import { groupBy } from 'lodash'

import {
  getMonthlyProjectionFactor,
  getReasonType,
  isAgentInsight,
  isRunningExperiment,
} from './AgenticInsights.helpers'

// Builds the Explore tab's descriptive breakdown of a run's calls, grouped by
// conversion opportunity.
//
// What is REAL here (from each insight's `opportunityStats`):
//   the converted / didn't-convert / inconclusive split WITHIN an opportunity,
//   and each notable reason's share of decided calls (`pctOfConclusive`).
//
// Monthly counts are projected from the sample via the run's
// monthlyProjectionFactor (sample size → org-wide monthly volume).
//
// KNOWN GAP / SKEW (TODO(backend) — the number-skew gate):
//   The pipeline oversamples some opportunities for per-agent analysis. Once the
//   backend un-skew (Fix A) is deployed, `opportunityStats.totalCalls` reflects
//   the uniform sample and is comparable across opportunities. Until then we
//   still normalize volume to sum to 100% across the shown opportunities so the
//   overview bar can't exceed 100% against a not-yet-deployed backend.
const pctOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0)

const buildOpportunity = (name, group, monthlyFactor) => {
  const stats = group.find((insight) => insight.opportunityStats)?.opportunityStats || {}
  const totalCalls = stats.totalCalls ?? 0
  const conclusiveCalls = stats.conclusiveCalls ?? 0
  const convertedCalls = stats.convertedCalls ?? 0

  // Clamp against inconsistent resampled counts, then normalize so the three
  // shares always sum to 100% (the composition bar must fill cleanly).
  const rawConverted = Math.max(0, pctOf(convertedCalls, totalCalls))
  const rawInconclusive = Math.max(0, pctOf(totalCalls - conclusiveCalls, totalCalls))
  const rawDidnt = Math.max(0, pctOf(conclusiveCalls - convertedCalls, totalCalls))
  const compSum = rawConverted + rawInconclusive + rawDidnt || 1
  const convertedPct = (rawConverted / compSum) * 100
  const inconclusivePct = (rawInconclusive / compSum) * 100
  const didntConvertPct = (rawDidnt / compSum) * 100

  const reasons = group.map((insight) => {
    const pctOfConclusive = (insight.pctOfConclusive ?? 0) * 100
    return {
      insightId: insight.id,
      name: insight.conversionReason || insight.title,
      type: getReasonType(insight),
      pctOfConclusive,
      shareOfOpportunityPct: pctOfConclusive * pctOf(conclusiveCalls, totalCalls) * 0.01,
      hasInsight: isAgentInsight(insight) && !!insight.recommendation,
      hasRunningExperiment: isRunningExperiment(insight),
      monthlyLost: Math.round((insight.pctOfConclusive ?? 0) * conclusiveCalls * monthlyFactor),
    }
  })

  const knownLossPct = reasons.reduce((sum, reason) => sum + reason.shareOfOpportunityPct, 0)

  return {
    // `name` stays the raw opportunity name — it's the stable grouping/jump key
    // (two opportunities could get the same friendly display name). `label` is
    // what we show.
    name,
    label:
      group.find((insight) => insight.conversionOpportunityDisplayName)
        ?.conversionOpportunityDisplayName || name,
    description: group.find((insight) => insight.conversionOpportunityDescription)
      ?.conversionOpportunityDescription,
    totalCalls,
    convertedPct,
    inconclusivePct,
    didntConvertPct,
    convertedCalls,
    reasons,
    otherLossPct: Math.max(0, didntConvertPct - knownLossPct),
    // Set during normalization below.
    volumeSharePct: 0,
    monthlyCalls: 0,
  }
}

export const buildRunBreakdown = (insights) => {
  const runInsight = insights.find((insight) => insight.pipelineStats)
  const sampleCalls = runInsight?.pipelineStats?.totalCalls ?? 0
  const monthlyFactor = getMonthlyProjectionFactor(runInsight)
  const noOppCalls = runInsight?.pipelineStats?.noOpportunityCalls ?? 0
  const byOpportunity = groupBy(
    insights,
    (insight) => insight.conversionOpportunity || 'Uncategorized'
  )
  const opportunities = Object.entries(byOpportunity)
    .map(([name, group]) => buildOpportunity(name, group, monthlyFactor))
    .sort((oppA, oppB) => oppB.totalCalls - oppA.totalCalls)

  // Normalize volume across the shown opportunities AND the no-opportunity
  // bucket, so shares sum to 100% and the overview bar partitions the whole
  // sample (see skew note above). Self-normalizing, so it stays bounded whether
  // or not the backend un-skew (Fix A) is deployed yet.
  const totalCalls = opportunities.reduce((sum, opp) => sum + opp.totalCalls, noOppCalls)
  opportunities.forEach((opp) => {
    opp.volumeSharePct = totalCalls > 0 ? (opp.totalCalls / totalCalls) * 100 : 0
    opp.monthlyCalls = Math.round(opp.totalCalls * monthlyFactor)
  })

  const noOpportunity = {
    calls: noOppCalls,
    volumeSharePct: totalCalls > 0 ? (noOppCalls / totalCalls) * 100 : 0,
    monthlyCalls: Math.round(noOppCalls * monthlyFactor),
  }

  return { sampleCalls, opportunities, noOpportunity }
}
